import { MapStyleService } from './../style/map-style.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AnalyticsEventType } from '../../google-analytics/analytics-event-type';
import { MtnMap } from '../mtn-map';
import { Feature } from '../../core/models';
import { MapMode } from '../map-mode.enum';
import { BaseComponent } from '../../core/base-component';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MapEvent, MapEventType } from '../map-events';
import MeasureTool from 'measuretool-googlemaps-v3/dist/gmaps-measuretool.umd.js';

import LatLngBounds = google.maps.LatLngBounds;
import LatLngLiteral = google.maps.LatLngLiteral;
import MapTypeId = google.maps.MapTypeId;
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
import { measurementTimer } from 'src/app/core/constants/constants';

@Component({
  standalone: false,
  selector: 'mtn-map-controls-toolbar',
  templateUrl: './map-controls-toolbar.component.html',
  styleUrls: ['./map-controls-toolbar.component.scss']
})
export class MapControlsToolbarComponent extends BaseComponent implements OnInit {

  @Input()
  map: MtnMap;
  AnalyticsEventType = AnalyticsEventType;
  MapMode = MapMode;
  MapTypeId = MapTypeId;
  @Output() buttonClicked = new EventEmitter<boolean>();

  isFeatureMap = false;
  isMapTypeControlsEnabled = false;
  _isStoreAdding = false;
  _isMeasurementEnabled = false;
  distance = 0;
  measureTool!: MeasureTool;

  private isMapReinitializing = false;
  private previousMapInstance: any;

  constructor(private mapStyleServise: MapStyleService, private hitTracking: HitTrackingService,) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToMapEvents();
    this.previousMapInstance = this.map;
  }

  centerOnSelection(): void {
    this.disablePNCIfActive();
    const points = this.map.selectedFeatures.map((feature: Feature) => feature.getGeometryAsLatLng());

    const bounds = new LatLngBounds();
    points.forEach((point: LatLngLiteral) => bounds.extend(point));

    this.map.fitBounds(bounds, 120);

    if (this.map.options.recenterOffsetX) {
      this.map.panBy(this.map.options.recenterOffsetX / 2, 0);
    }
  }

  toggleDarkMode(): void {
    this.disablePNCIfActive();
    this.isMapReinitializing = true;

    if (this.measureTool && this._isMeasurementEnabled) {
      this.cleanupMeasureTool();
    }

    this.map.setOptions({
      mode: this.map.options.mode === MapMode.CLASSIC ? MapMode.DARK : MapMode.CLASSIC,
    });
    this.mapStyleServise.updateStyle(!this.mapStyleServise.getCurrentStyle());

    measurementTimer.shortDelay().subscribe(() => {
      this.isMapReinitializing = false;
      if (this._isMeasurementEnabled) {
        this.mapMeasureTool();
      }
    });
  }

  togglestoreAdd() {
    this._isStoreAdding = !this._isStoreAdding;

    // If just enabled → send enable tracking
    if (this._isStoreAdding) {

      this.hitTracking.hitTheUserTracking(
        USER_TRACKING_SOURCES.MAP_CONTROLS_TOOLBAR,
        'map pin drop mode enabled',
        USER_TRACKING_ACTIONS.PIN_DROP_ENABLED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.PIN_DROP_ENABLED]
      ).subscribe(() => {});
    } else {
      this.hitTracking.hitTheUserTracking(
        USER_TRACKING_SOURCES.MAP_CONTROLS_TOOLBAR,
        'map pin drop mode disabled',
        USER_TRACKING_ACTIONS.PIN_DROP_MODE_DISABLED,
        USER_TRACKING_ACTION_DESCRIPTIONS.PIN_DROP_MODE_DISABLED
      ).subscribe(() => {});
    }

    this.buttonClicked.emit(this._isStoreAdding);
    this.map.setOptions({
      _isStoreAdding: this._isStoreAdding,
    });
  }

  toggleDistanceMeasurement() {
    this.disablePNCIfActive();
    this._isMeasurementEnabled = !this._isMeasurementEnabled;

    if (this._isMeasurementEnabled) {
      this.mapMeasureTool();
    } else {
      this.cleanupMeasureTool();
    }

    this.map.setOptions({
      _isMeasurementEnabled: this._isMeasurementEnabled,
    });
  }

  mapMeasureTool() {
    try {
      this.cleanupMeasureTool();

      if (this.isMapReinitializing) {
        measurementTimer.mediumDelay().subscribe(() => this.initializeMeasureTool());
      } else {
        this.initializeMeasureTool();
      }
    } catch (err) {
      console.error('Error initializing MeasureTool:', err);
    }
  }

  private initializeMeasureTool() {
    try {
      this.measureTool = new MeasureTool(this.map, {
        showSegmentLength: true,
        contextMenu: false,
        unit: MeasureTool.UnitTypeId.IMPERIAL,
      });

      this.onStart();
    } catch (err) {
      console.error('Error creating MeasureTool instance:', err);

      measurementTimer.longDelay().subscribe(() => {
        if (this._isMeasurementEnabled) {
          this.initializeMeasureTool();
        }
      });
    }
  }

  private cleanupMeasureTool() {
    if (this.measureTool) {
      try {
        this.measureTool.end();
        this.measureTool = null!;
      } catch (err) {
        console.error('Error cleaning up MeasureTool:', err);
      }
    }
  }

  onStart() {
    if (this.measureTool) {
      this.measureTool.start();
    }
  }

  onEnd() {
    this.cleanupMeasureTool();
  }

  toggleSatelliteView(): void {
    this.disablePNCIfActive();
    this.isMapReinitializing = true;

    if (this.measureTool && this._isMeasurementEnabled) {
      this.cleanupMeasureTool();
    }

    this.map.setOptions({
      mapTypeId: this.map.options.mapTypeId === MapTypeId.HYBRID ? MapTypeId.ROADMAP : MapTypeId.HYBRID
    });

    measurementTimer.shortDelay().subscribe(() => {
      this.isMapReinitializing = false;
      if (this._isMeasurementEnabled) {
        this.mapMeasureTool();
      }
    });
  }

  private setControlFlags(): void {
    this.isFeatureMap = this.map.options.isFeatureMap;
    this.isMapTypeControlsEnabled = this.map.options.controlConfiguration.isMapModeEnabled
      || this.map.options.controlConfiguration.isMapTypeEnabled;
  }

  private subscribeToMapEvents(): void {
    this.addSubscription(
      this.map.events
        .pipe(
          filter((event: MapEvent<any>) => event.type === MapEventType.OPTION_CHANGE),
          debounceTime(150),
          filter(() => !this.isMapReinitializing),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        )
        .subscribe(() => {
          this.setControlFlags();

          if (this.previousMapInstance !== this.map) {
            this.previousMapInstance = this.map;

            if (this._isMeasurementEnabled && !this.measureTool) {
              measurementTimer.mediumDelay().subscribe(() => {
                if (this._isMeasurementEnabled) {
                  this.mapMeasureTool();
                }
              });
            }
          }
        })
    );

    this.addSubscription(
      this.map.events
        .pipe(
          filter((event: MapEvent<any>) => event.type === MapEventType.SELECTION_MODE_CHANGE)
        )
        .subscribe(() => {
          if (this._isMeasurementEnabled) {
            this._isMeasurementEnabled = false;
            this.cleanupMeasureTool();
            this.map.setOptions({
              _isMeasurementEnabled: this._isMeasurementEnabled,
            });
          }
        })
    );
  }

  public disablePNCIfActive(): void {
    if (this._isStoreAdding) {
      this._isStoreAdding = false;
      this.buttonClicked.emit(this._isStoreAdding);
      this.map.setOptions({
        _isStoreAdding: this._isStoreAdding,
      });
    }
  }
}
